import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { streamLiveEvents, type LiveEvent } from '../api/events';
import { billingApi } from '../api/billing';
import { billingKeys } from './useBilling';

/** One mounted bridge owns the stream; query hooks only observe connectivity. */
export const useLiveConnection = create<{ connected: boolean }>(() => ({ connected: false }));

export function useLiveEvents({ onEvent, token, enabled = true, streamFetch }: {
  onEvent: (event: LiveEvent) => void;
  token: string | null;
  enabled?: boolean;
  streamFetch?: typeof fetch;
}) {
  const queryClient = useQueryClient();
  const handler = useRef(onEvent);
  handler.current = onEvent;
  const [unavailable, setUnavailable] = useState(false);
  const connected = useLiveConnection((state) => state.connected);
  const billing = useQuery({ queryKey: billingKeys.status(), queryFn: billingApi.status, enabled: Boolean(token) && enabled });
  // Reconnect after an actual plan change, never every status refetch/resync.
  const plan = billing.data?.data?.effective_plan;
  useEffect(() => {
    const controller = new AbortController();
    setUnavailable(false);
    useLiveConnection.setState({ connected: false });
    if (token && enabled) {
      void streamLiveEvents({
        signal: controller.signal, streamFetch,
        onConnected: (value) => { if (!controller.signal.aborted) useLiveConnection.setState({ connected: value }); },
        onResync: () => { void queryClient.invalidateQueries({ type: 'active' }); },
        onEvent: (event) => {
          if (event.domain === 'billing' || event.action === 'plan_changed') void queryClient.invalidateQueries({ queryKey: billingKeys.status() });
          handler.current(event);
        },
      }).then((result) => { if (!controller.signal.aborted) setUnavailable(result === 'upgrade_required'); });
    }
    return () => { controller.abort(); useLiveConnection.setState({ connected: false }); };
  }, [token, enabled, plan, streamFetch, queryClient]);
  return { connected, unavailable };
}
